import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../../../api/client";
import { Label } from "../../../components/ui/label";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { Link } from "react-router-dom";
import { CheckCircle2, Search, X } from "lucide-react";

const normalize = (value) => String(value || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLocaleLowerCase("pt-BR");

export default function Step1Client({ data, setData }) {
  const [clients, setClients] = useState([]);
  const [client, setClient] = useState(null);
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const preselectionApplied = useRef(false);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data || [])).catch(() => setClients([]));
  }, []);

  // A Central do Cliente abre /propostas/nova?client_id=<id>. A pré-seleção é
  // aplicada somente uma vez: depois disso o usuário continua livre para limpar e
  // pesquisar outro cliente no mesmo campo.
  useEffect(() => {
    if (preselectionApplied.current) return;
    preselectionApplied.current = true;
    const requested = new URLSearchParams(window.location.search).get("client_id");
    if (!requested || data.client_id) return;
    setData((current) => ({ ...current, client_id: requested }));
  }, [data.client_id, setData]);

  useEffect(() => {
    if (!data.client_id) {
      setClient(null);
      return;
    }
    const selected = clients.find((item) => item.id === data.client_id) || null;
    setClient(selected);
    if (!selected) return;
    setQuery(selected.razao_social || selected.nome_fantasia || "");
    if (Number(data.acompanhamento?.num_funcionarios || 0) === 0 && Number(selected.num_funcionarios || 0) > 0) {
      setData((current) => ({
        ...current,
        acompanhamento: {
          ...current.acompanhamento,
          num_funcionarios: selected.num_funcionarios || 0,
        },
      }));
    }
  }, [data.client_id, clients, data.acompanhamento?.num_funcionarios, setData]);

  const filteredClients = useMemo(() => {
    const needle = normalize(query).trim();
    const digits = String(query || "").replace(/\D/g, "");
    const matches = clients.filter((item) => {
      if (!needle && !digits) return true;
      const text = normalize([
        item.razao_social,
        item.nome_fantasia,
        item.cnpj,
        item.municipio,
        item.uf,
      ].filter(Boolean).join(" "));
      const cnpjDigits = String(item.cnpj || "").replace(/\D/g, "");
      return (needle && text.includes(needle)) || (digits.length >= 3 && cnpjDigits.includes(digits));
    });
    return matches.slice(0, 10);
  }, [clients, query]);

  const chooseClient = (selected) => {
    setClient(selected);
    setQuery(selected.razao_social || selected.nome_fantasia || "");
    setFocused(false);
    setData((current) => ({
      ...current,
      client_id: selected.id,
      acompanhamento: {
        ...current.acompanhamento,
        num_funcionarios: Number(current.acompanhamento?.num_funcionarios || 0) > 0
          ? current.acompanhamento.num_funcionarios
          : (selected.num_funcionarios || 0),
      },
    }));
  };

  const clearClient = () => {
    setClient(null);
    setQuery("");
    setFocused(true);
    setData((current) => ({ ...current, client_id: "" }));
  };

  const onSearchChange = (value) => {
    setQuery(value);
    setFocused(true);
    if (client && value !== (client.razao_social || client.nome_fantasia || "")) {
      setClient(null);
      setData((current) => ({ ...current, client_id: "" }));
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="wizard-step-1">
      <div>
        <h2 className="text-xl font-medium">Dados do Cliente</h2>
        <p className="text-sm text-muted-foreground">Pesquise pelo nome, nome fantasia, CNPJ ou município. Ao iniciar pela Central do Cliente, ele já vem selecionado.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_auto]">
        <div>
          <Label className="label-tech">Cliente *</Label>
          <div className="mt-1.5 rounded-xl border border-border bg-background p-2" data-testid="step1-client-search-box">
            <div className="flex items-center gap-2">
              <Search className="ml-1 size-4 shrink-0 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => onSearchChange(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => window.setTimeout(() => setFocused(false), 120)}
                placeholder="Digite para pesquisar o cliente..."
                className="border-0 shadow-none focus-visible:ring-0"
                autoComplete="off"
                data-testid="step1-client-search"
              />
              {data.client_id && (
                <Button type="button" variant="ghost" size="icon" onClick={clearClient} aria-label="Limpar cliente selecionado" data-testid="step1-client-clear">
                  <X className="size-4" />
                </Button>
              )}
            </div>

            {focused && (
              <div className="mt-2 max-h-72 overflow-y-auto rounded-lg border border-border bg-card" data-testid="step1-client-results">
                {filteredClients.length ? filteredClients.map((item) => {
                  const selected = item.id === data.client_id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={`flex w-full items-start justify-between gap-3 border-b border-border/60 px-3 py-3 text-left last:border-0 hover:bg-muted/35 ${selected ? "bg-primary/[0.06]" : ""}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => chooseClient(item)}
                      data-testid={`step1-client-option-${item.id}`}
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium">{item.razao_social || item.nome_fantasia || "Cliente sem razão social"}</span>
                        <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                          {[item.nome_fantasia, item.cnpj, [item.municipio, item.uf].filter(Boolean).join("/")].filter(Boolean).join(" · ") || "Cadastro sem dados complementares"}
                        </span>
                      </span>
                      {selected && <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" />}
                    </button>
                  );
                }) : (
                  <div className="px-4 py-5 text-center text-sm text-muted-foreground">Nenhum cliente encontrado para “{query}”.</div>
                )}
              </div>
            )}
          </div>
          {client && (
            <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-emerald-500/25 bg-emerald-500/[0.06] px-3 py-1.5 text-xs text-emerald-700 dark:text-emerald-300" data-testid="step1-client-selected">
              <CheckCircle2 className="size-3.5" /> Cliente selecionado: {client.razao_social || client.nome_fantasia}
            </div>
          )}
        </div>

        <div className="flex items-end">
          <Link to="/clientes/novo">
            <Button variant="outline" data-testid="step1-new-client-link">+ Novo cliente</Button>
          </Link>
        </div>
      </div>

      {client && (
        <div className="bg-muted/30 border border-border rounded-lg p-5 grid md:grid-cols-3 gap-4 text-sm" data-testid="step1-client-summary">
          <div><div className="label-tech">CNPJ</div><div className="font-mono">{client.cnpj || "-"}</div></div>
          <div><div className="label-tech">Atividade</div><div>{client.atividade_principal || "-"}</div></div>
          <div><div className="label-tech">CNAE</div><div className="font-mono">{client.cnae || client.cnae_principal || "-"}</div></div>
          <div><div className="label-tech">Município</div><div>{[client.municipio, client.uf].filter(Boolean).join("/") || "-"}</div></div>
          <div><div className="label-tech">Funcionários</div><div className="font-mono">{client.num_funcionarios || 0}</div></div>
          <div><div className="label-tech">Grau de Risco</div><div className="font-mono">{client.grau_risco || "-"}</div></div>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <Label className="label-tech">Data da proposta</Label>
          <Input type="date" value={data.data} onChange={(e) => setData({ ...data, data: e.target.value })} data-testid="step1-data-input" />
        </div>
        <div>
          <Label className="label-tech">Validade (dias)</Label>
          <Input type="number" min="1" value={data.validade_dias} onChange={(e) => setData({ ...data, validade_dias: parseInt(e.target.value || 15, 10) })} data-testid="step1-validade-input" />
        </div>
        <div>
          <Label className="label-tech">Duração contrato (meses)</Label>
          <Input type="number" min="1" value={data.duracao_meses} onChange={(e) => setData({ ...data, duracao_meses: parseInt(e.target.value || 12, 10) })} data-testid="step1-duracao-input" />
        </div>
      </div>
    </div>
  );
}
